'use client';

/* eslint-disable @next/next/no-img-element -- Use the approved whole packaging render with its original framing. */

import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight } from '@phosphor-icons/react';
import { formatPrice, pluralize } from '@/lib/utils';
import type { RouletteConfig } from '@/lib/api/roulette';
import { useCartStore } from '@/stores/cart-store';
import type { RoulettePackagingDialog } from '@/components/features/cart/roulette-concepts/roulette-packaging-options';
import { CLOSED_BOX, prizeArt } from '@/components/features/cart/roulette-concepts/roulette-prize-art';
import './roulette-catalog-card.css';

type PreviewComponent = typeof RoulettePackagingDialog;
let cachedPreview: PreviewComponent | null = null;
let previewRequest: Promise<PreviewComponent> | null = null;
// The order assistant opens the same box from its chat, so the loader is shared.
export function loadRouletteBox() {
  if (cachedPreview) return Promise.resolve(cachedPreview);
  if (!previewRequest) {
    previewRequest =
      import('@/components/features/cart/roulette-concepts/roulette-packaging-options')
        .then((module) => {
          cachedPreview = module.RoulettePackagingDialog;
          return cachedPreview;
        })
        .catch((error) => {
          previewRequest = null;
          throw error;
        });
  }
  return previewRequest;
}

export function RouletteCatalogCard({ config }: { config: RouletteConfig }) {
  const [Preview, setPreview] = useState<PreviewComponent | null>(() => cachedPreview);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const opener = useRef<HTMLButtonElement | null>(null);
  const alive = useRef(true);
  const pending = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const count = new Set(config.products.map((product) => product.id)).size;
  const rolls = `${count} ${pluralize(count, 'ролла', 'роллов', 'роллов')}`;
  // Keep the approved California cover; adding more prize renders must not change this card.
  // It is shown only while that roll remains in the pool with its original catalog photo.
  const coverProduct = config.products.find((product) => product.id === 100133);
  const example = coverProduct ? prizeArt(coverProduct).openBox : null;
  const inCart = useCartStore(
    (s) => s._hasHydrated && s.items.some((item) => item.bonusType === 'roulette')
  );
  const show = async (button: HTMLButtonElement) => {
    if (pending.current) return;
    opener.current = button;
    if (Preview) {
      setOpen(true);
      return;
    }
    pending.current = true;
    setLoading(true);
    setFailed(false);
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      const component = await Promise.race([
        loadRouletteBox(),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => reject(new Error('Preview load timed out')), 10000);
        }),
      ]);
      if (alive.current) {
        setPreview(() => component);
        setOpen(true);
      }
    } catch {
      if (alive.current) setFailed(true);
    } finally {
      clearTimeout(timeout);
      if (alive.current) {
        pending.current = false;
        setLoading(false);
      }
    }
  };

  return (
    <article className="product-card scene-card scene-master roulette-catalog-card" data-roulette-card aria-busy={loading}>
      <div className="card-backdrop" aria-hidden="true">
        <span className="roulette-card-light" />
      </div>
      <button
        className="food-button"
        onClick={(event) => void show(event.currentTarget)}
        disabled={loading}
        onPointerEnter={() => {
          void loadRouletteBox().catch(() => undefined);
        }}
        aria-label={
          inCart
            ? 'Открыть рулетку: ролл уже в корзине'
            : `Открыть рулетку: один из ${rolls} за ${formatPrice(config.price)}`
        }
      >
        <span className="roulette-card-count">{inCart ? 'В корзине' : `1 из ${count}`}</span>
        <img
          className="roulette-card-box"
          src={example ?? CLOSED_BOX}
          width={1536}
          height={1024}
          alt={
            example
              ? 'Фирменная коробка с роллами — пример сюрприза в рулетке'
              : 'Фирменная коробка с сюрпризом'
          }
          loading="lazy"
          decoding="async"
        />
      </button>
      <div className="card-copy">
        <h3>
          <button onClick={(event) => void show(event.currentTarget)} disabled={loading}>
            Рулетка<span>Ролл-сюрприз</span>
          </button>
        </h3>
        <div className="card-actions">
          <button
            className="price-pill"
            onClick={(event) => void show(event.currentTarget)}
            disabled={loading}
            aria-label={
              failed
                ? 'Повторить открытие рулетки'
                : `Рулетка: ролл-сюрприз за ${formatPrice(config.price)}`
            }
          >
            {loading ? (
              'Открываем…'
            ) : failed ? (
              'Повторить'
            ) : (
              <>
                {formatPrice(config.price)}
                <ArrowUpRight size={17} aria-hidden="true" />
              </>
            )}
          </button>
        </div>
        {failed && (
          <p className="roulette-card-error" role="status">
            Не удалось открыть. Попробуйте ещё раз.
          </p>
        )}
      </div>
      {Preview && open && (
        <Preview
          open={open}
          onOpenChange={setOpen}
          config={config}
          onReturnFocus={() => opener.current?.focus({ preventScroll: true })}
        />
      )}
    </article>
  );
}
